import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Star } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    customer_name: '',
    rating: 5,
    comment: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API}/reviews?visible_only=true`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(`${API}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({
          title: "¡Gracias por tu opinión!",
          description: "Tu reseña ha sido publicada"
        });
        setFormData({ customer_name: '', rating: 5, comment: '' });
        fetchReviews();
      } else {
        throw new Error('Error al enviar reseña');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, onRate = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-6 w-6 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-slate-600'
            } ${interactive ? 'cursor-pointer hover:text-yellow-400' : ''}`}
            onClick={() => interactive && onRate && onRate(star)}
          />
        ))}
      </div>
    );
  };

  const featuredReviews = reviews.filter(r => r.featured);
  const normalReviews = reviews.filter(r => !r.featured);

  return (
    <div className="min-h-screen bg-slate-950 py-20 px-4">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Opiniones de Nuestros Clientes
          </h1>
          <p className="text-lg text-slate-400">
            Lee lo que dicen nuestros clientes sobre nuestros servicios
          </p>
        </div>

        {/* Featured Reviews */}
        {featuredReviews.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Star className="h-6 w-6 fill-yellow-400 text-yellow-400" />
              Reseñas Destacadas
            </h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {featuredReviews.map((review) => (
                <Card key={review.id} className="bg-gradient-to-br from-cyan-900/20 to-emerald-900/20 border-cyan-700">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-white">{review.customer_name}</CardTitle>
                        <CardDescription className="text-slate-400">
                          {new Date(review.created_at).toLocaleDateString('es-AR')}
                        </CardDescription>
                      </div>
                      {renderStars(review.rating)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-300">{review.comment}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* All Reviews */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {normalReviews.map((review) => (
            <Card key={review.id} className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-white text-lg">{review.customer_name}</CardTitle>
                    <CardDescription className="text-slate-400 text-sm">
                      {new Date(review.created_at).toLocaleDateString('es-AR')}
                    </CardDescription>
                  </div>
                  {renderStars(review.rating)}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-300 text-sm">{review.comment}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Review Form */}
        <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-2xl">Deja tu Opinión</CardTitle>
            <CardDescription className="text-slate-400">
              Comparte tu experiencia con nosotros
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-white">Tu Nombre</Label>
                <Input
                  id="name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="bg-slate-900/50 border-slate-700 text-white"
                  required
                />
              </div>

              <div>
                <Label className="text-white mb-2 block">Calificación</Label>
                {renderStars(formData.rating, true, (rating) => setFormData({ ...formData, rating }))}
              </div>

              <div>
                <Label htmlFor="comment" className="text-white">Tu Opinión</Label>
                <Textarea
                  id="comment"
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  className="bg-slate-900/50 border-slate-700 text-white min-h-32"
                  placeholder="Cuéntanos sobre tu experiencia..."
                  required
                />
              </div>

              <Button 
                type="submit" 
                disabled={submitting}
                className="w-full bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500"
              >
                {submitting ? 'Enviando...' : 'Enviar Opinión'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Reviews;
